import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getExternalTrades, UNBOOKED_TRADE_STATUSES } from '../api/external-trade';
import { getPositionSnapshots, UNSETTLED_SNAPSHOT_STATUSES } from '../api/position-snapshot';
import {
  ACTIVE_PARSE_REQUEST_STATUSES,
  getParseRequests,
  PARSE_REQUEST_TYPES,
  type ParseRequestResponse,
} from '../api/parse-request';
import {
  PORTFOLIO_DETAIL_REFETCH_OPTIONS,
  PORTFOLIO_DETAIL_RETRY,
  portfolioDetailQueryKeys,
} from './use-portfolio-detail-queries';
import { deriveTimingFlags } from '../lib/poll-timing';
import type { ScopeContext } from '../../auth/model/types';

/**
 * The cadence while the book is settling. Booking normally lands within seconds of the
 * row being recorded, so the first half minute checks often enough for the reader to see
 * the table fill in nearly as it happens.
 */
const FAST_POLL_MS = 3_000;

/**
 * How long the fast cadence lasts. A book still settling after thirty seconds is waiting
 * on a queue retry, not on the ordinary path, and a retry is minutes apart — checking
 * every three seconds past this point only costs requests.
 */
const FAST_POLL_WINDOW_MS = 30_000;

/** The cadence after the fast window: often enough to catch a retry landing, cheap enough
 *  to leave running through one. */
const SLOW_POLL_MS = 10_000;

/**
 * Past this, the marker says booking is taking longer than usual. Two minutes covers a
 * few queue retries; a book that has not moved by then is stuck on something the reader
 * cannot see from here, and the copy should stop implying it is about to finish.
 *
 * Named after the import poll's own `STALL_THRESHOLD_MS` (see
 * `use-parse-import-queries.ts`), whose `deriveTimingFlags` (`../lib/poll-timing.ts`)
 * this hook now shares. The mechanism is shared; the VALUE is not: the import poll's
 * threshold is matched to the parser's and the attachment cell's own recovery windows,
 * while this one is matched to the ledger booking path's own recovery window — see
 * `POLL_TIMEOUT_MS` below.
 */
const STALL_THRESHOLD_MS = 2 * 60_000;

/**
 * Past this the poll stops outright. Ten minutes is not an arbitrary ceiling: it is the
 * interval `b-cell-position-v1`'s `BookExternalTradeCron` (`GET /private/book-external-
 * trade`) and `ReconcilePositionCron` (`GET /private/reconcile-position`) already run on
 * (position README, Async Flows) — a book still stuck past this point has already been
 * re-driven by one dead-letter pass. A page left open on a portfolio whose ledger is down
 * would otherwise keep issuing six reads every ten seconds for as long as the tab lives;
 * the reader keeps the "taking longer" copy and a Check again button, which re-arms this
 * clock. Kept greater than `STALL_THRESHOLD_MS` so the slow copy is always seen before
 * the poll stops.
 */
const POLL_TIMEOUT_MS = 10 * 60_000;

export interface BookingCounts {
  unbookedTradeCount: number;
  unsettledSnapshotCount: number;
  runningImportCount: number;
  /** Some read returned a cursor: every count above is a lower bound, never a proven
   *  final figure. A cursor is the ONLY thing that sets this — a read that failed says
   *  nothing about pagination and is counted separately, in `failedImportReadCount`. */
  isPartial: boolean;
  /** How many of this tick's running-import reads rejected — one per parse-request type
   *  per active status (see `readBookingCounts`), never a fixed number written out here,
   *  since that family/status product is free to grow. A rejected read contributes no
   *  rows to `runningImportCount`, so that count is unverified while this is above zero
   *  — but the trade and snapshot counts are unaffected, and a failed read is not a
   *  cursor: it never widens `isPartial`, never makes the book "settling" on its own, and
   *  never renders a count as a lower bound. See `readBookingCounts`. */
  failedImportReadCount: number;
  /** The ids on this tick's unbooked-trade page, oldest first — see `readBookingCounts`.
   *  Used only to detect progress on a page pinned at the cap; never rendered. */
  unbookedTradeIds: string[];
  unsettledSnapshotIds: string[];
  runningImportIds: string[];
  /** The earliest `created` among every row this tick fetched, across every
   *  listing — `null` when the tick fetched no rows at all (the uncounted-partial and
   *  incomplete cases). Anchors how long the OLDEST still-pending row has waited, which survives a
   *  remount the way a component-local clock cannot — see `useBookingStatus`. */
  oldestCreated: string | null;
}

export interface BookingStatus {
  /** `null` while unknown or before the first answer — never a zero standing in for
   *  "could not check". */
  counts: BookingCounts | null;
  isSettling: boolean;
  /** The book has neither moved nor been re-checked for longer than `STALL_THRESHOLD_MS`. */
  isSlow: boolean;
  /** The poll stopped at `POLL_TIMEOUT_MS` with the book still settling and no move or
   *  check since. */
  isTimedOut: boolean;
  /** The check itself failed; nothing is known about the book. */
  isUnknown: boolean;
  /** The check answered and the book is not settling, but at least one running-import
   *  read failed this tick (`counts.failedImportReadCount > 0`): the trade and snapshot
   *  reads are proven empty and unpaginated, only the running imports are unverified.
   *  No poll runs in this state — `isSettlingCounts` does not count a failed read — so
   *  the marker's Check again is the only way to a verified answer. */
  isIncomplete: boolean;
  /** The check has answered at least once, successfully or not (`counts != null ||
   *  isUnknown`). A figure rendered before this is not yet a fact of any kind — settled,
   *  booking, or unknown — so every reader that could show a figure as final holds it
   *  behind a skeleton until this flips true, the same rule for all of them. */
  hasAnswered: boolean;
  /** A check is in flight (for `aria-busy`). */
  isChecking: boolean;
  /** Check again: re-arms the clock and refetches. */
  check: () => void;
}

export type BookingState = 'settling' | 'slow' | 'timed-out' | 'unknown' | 'incomplete';

/**
 * The one state a marker shows, from the flags above. Unknown wins over everything: a
 * check that failed knows nothing, including whether the book was slow. Timed-out wins
 * over slow because it is the later, stricter reading of the same clock. Incomplete
 * comes after every settling state: a settling book with a failed import read is still
 * settling (the marker names the unchecked imports beside the counts it does have), and
 * only a book with nothing left to settle is reported as merely unverified. `null` is
 * the settled book, where no marker is rendered at all.
 */
export function bookingStateOf(status: BookingStatus): BookingState | null {
  if (status.isUnknown) return 'unknown';
  if (status.isTimedOut) return 'timed-out';
  if (status.isSlow) return 'slow';
  if (status.isSettling) return 'settling';
  if (status.isIncomplete) return 'incomplete';
  return null;
}

export interface UseBookingStatusOptions {
  sessionId: string | null;
  accountScope: ScopeContext | null;
  portfolioId: string | undefined;
  /** Called once per tick that shows progress — real movement or a cleared uncertainty
   *  (`hasProgressed`) — so the page can reload the figures the booking just changed.
   *  Never re-checks booking itself — this poll re-checks on its own clock; see `check`
   *  for the reader-action path that does. */
  onProgress?: () => void;
}

/** The earliest `created` timestamp across every row in the given lists, as an ISO
 *  string — `null` when none of them carried a parseable one. Used to anchor the
 *  settling clock to the oldest still-pending row rather than to local watch time; see
 *  `useBookingStatus`. */
function earliestCreated(...rowLists: { created: string }[][]): string | null {
  let earliestMs: number | null = null;
  let earliest: string | null = null;
  for (const rows of rowLists) {
    for (const row of rows) {
      const ms = new Date(row.created).getTime();
      if (!Number.isFinite(ms)) continue;
      if (earliestMs == null || ms < earliestMs) {
        earliestMs = ms;
        earliest = row.created;
      }
    }
  }
  return earliest;
}

/**
 * The listings that together say whether the figures on the page are final: the
 * portfolio's unbooked trades, its unsettled snapshots, and its running imports of both
 * parse-request types. The trade and snapshot reads filter server-side to this
 * portfolio and are ordered oldest-first (`order_by=created`): a page capped at the
 * backend default holds the rows that book next, so the read a large import keeps
 * returning is the one whose ids actually change as the front of the queue clears — see
 * `hasProgressed`. The parse-request reads are one per request type per active status —
 * each request type has its own listing and the parser's status filter is
 * single-valued — and also filter server-side to this portfolio via `portfolio_id`. A
 * running import is transient, so its first page is the whole set in practice — a
 * cursor anywhere is reported as `isPartial` rather than followed.
 *
 * The two core reads (trades, snapshots) are load-bearing for every portfolio page, so
 * a failure there still rejects the whole tick — the caller has nothing honest left to
 * show without them. The four parse-request reads are `Promise.allSettled` instead:
 * every portfolio page issues all of them regardless of whether that portfolio has ever
 * used either request type, so one type's route being unavailable (a rollout still in
 * progress, say) must not turn every portfolio-detail view uncheckable. A request type
 * whose reads fail contributes no rows to `runningImportCount`; the failures are
 * counted in `failedImportReadCount` and reported as exactly that — running imports
 * that could not be checked. They are NOT folded into `isPartial`: a failed read is not
 * a pagination fact, and reporting it as one made an outage on an empty portfolio read
 * as "rows beyond the first page may still be booking", with the figures marked
 * "Updating" and a poll running for ten minutes on a book that had nothing to settle.
 */
async function readBookingCounts(
  sessionId: string,
  accountScope: ScopeContext,
  portfolioId: string,
): Promise<BookingCounts> {
  const [tradesResult, snapshotsResult, ...importResults] = await Promise.allSettled([
    getExternalTrades(sessionId, accountScope, {
      portfolioId, statuses: UNBOOKED_TRADE_STATUSES, orderBy: 'created',
    }),
    getPositionSnapshots(sessionId, accountScope, {
      portfolioId, statuses: UNSETTLED_SNAPSHOT_STATUSES, orderBy: 'created',
    }),
    ...PARSE_REQUEST_TYPES.flatMap((requestType) =>
      ACTIVE_PARSE_REQUEST_STATUSES.map((status) =>
        getParseRequests(sessionId, accountScope, requestType, { status, portfolioId }))),
  ]);
  if (tradesResult.status === 'rejected') throw tradesResult.reason;
  if (snapshotsResult.status === 'rejected') throw snapshotsResult.reason;
  const trades = tradesResult.value;
  const snapshots = snapshotsResult.value;

  const importPages = importResults
    .filter((result): result is PromiseFulfilledResult<ParseRequestResponse> => result.status === 'fulfilled')
    .map((result) => result.value);
  const failedImportReadCount = importResults.length - importPages.length;
  const runningImports = importPages.flatMap((page) => page.requests);
  return {
    unbookedTradeCount: trades.trades.length,
    unsettledSnapshotCount: snapshots.snapshots.length,
    runningImportCount: runningImports.length,
    isPartial: Boolean(trades.cursor)
      || Boolean(snapshots.cursor)
      || importPages.some((page) => Boolean(page.cursor)),
    failedImportReadCount,
    unbookedTradeIds: trades.trades.map((trade) => trade.id),
    unsettledSnapshotIds: snapshots.snapshots.map((snapshot) => snapshot.id),
    runningImportIds: runningImports.map((request) => request.id),
    oldestCreated: earliestCreated(trades.trades, snapshots.snapshots, runningImports),
  };
}

/** A partial read is a lower bound, never a proven zero — a page-capped count of zero
 *  behind a cursor says nothing about what the next page holds. A failed import read is
 *  deliberately absent here: it leaves the book unverified, not settling, so nothing
 *  polls on it (see `refetchInterval`) and nothing reads it as booking in progress. */
function isSettlingCounts(counts: BookingCounts): boolean {
  return counts.isPartial
    || counts.unbookedTradeCount > 0
    || counts.unsettledSnapshotCount > 0
    || counts.runningImportCount > 0;
}

/** True when some id on the previous tick's page is no longer on this tick's page: it
 *  booked, settled, or finished. A page that only gained ids reports no departure here —
 *  see `hasProgressed` for why a page that only grows must never read as progress. */
function idLeftPage(previous: string[], current: string[]): boolean {
  const currentIds = new Set(current);
  return previous.some((id) => !currentIds.has(id));
}

/**
 * A count fell, or an id present on the previous tick's oldest-first page is gone from
 * this tick's: something at the front of the queue booked, settled, or finished. Count
 * comparison alone misses a page pinned at the backend's cap — the true remaining count
 * can fall for minutes while the page's own length stays unchanged — so the ids on each
 * oldest-first page are compared too. Only a departure counts as movement: a page that
 * only gained an id — an unrelated new trade recorded in the same portfolio while older
 * ones are stuck — must not read as movement, or a steady trickle of unrelated new rows
 * would mask a book that is genuinely stuck behind the slow/timed-out escalation this
 * feature exists to raise.
 *
 * The running-import signals (`runningImportCount`, `runningImportIds`) count only when
 * THIS tick's running-import reads all answered (`current.failedImportReadCount === 0`):
 * a failed read contributes no rows, so a fall in the count or a departure on that page
 * says nothing about the queue — it is the read that went missing, not the import that
 * finished. Gating on the current tick's own failure count, not on whether it grew from
 * the previous tick, also catches a same-count leg swap: one leg recovering while a
 * different leg starts failing leaves `failedImportReadCount` unchanged tick over tick,
 * but the running-import count it carries is still built from an incomplete read. The
 * trade and snapshot signals carry no such gate: a failed running-import read says
 * nothing about those two independent, always-load-bearing reads (see
 * `readBookingCounts`), so their movement — and the figures refresh it triggers — must
 * count on every tick, including one where an import leg 503s.
 */
function hasMoved(previous: BookingCounts, current: BookingCounts): boolean {
  const importReadsReliable = current.failedImportReadCount === 0;
  return current.unbookedTradeCount < previous.unbookedTradeCount
    || current.unsettledSnapshotCount < previous.unsettledSnapshotCount
    || idLeftPage(previous.unbookedTradeIds, current.unbookedTradeIds)
    || idLeftPage(previous.unsettledSnapshotIds, current.unsettledSnapshotIds)
    || (importReadsReliable
      && (current.runningImportCount < previous.runningImportCount
        || idLeftPage(previous.runningImportIds, current.runningImportIds)));
}

/**
 * A source of uncertainty this tick's counts carried has cleared: a page-capped read
 * resolved from uncertain to proven (`isPartial` falling with every raw count
 * unchanged), or the failed-import-read count fell back to zero. The figures it was
 * holding back become safe to show — the same way `isSettlingCounts` treats the
 * resolution as a fact about the book — so it counts for `onProgress`, refreshing the
 * figures once.
 *
 * It is NOT movement, and must never re-arm the settling clock the way `hasMoved` does:
 * a leg that fails and recovers repeatedly (flapping) would otherwise reset
 * `settlingSinceRef` on every recovery tick, defeating `STALL_THRESHOLD_MS` and
 * `POLL_TIMEOUT_MS` on a book that is, from the reader's perspective, not moving at all.
 * `useBookingStatus`'s effect calls this and `hasMoved` separately for exactly this
 * reason — see its own doc comment.
 */
function uncertaintyResolved(previous: BookingCounts, current: BookingCounts): boolean {
  return (previous.isPartial && !current.isPartial)
    || (previous.failedImportReadCount > 0 && current.failedImportReadCount === 0);
}

/** Whether this tick counts as progress at all — real movement (`hasMoved`) or a
 *  cleared uncertainty (`uncertaintyResolved`) — for firing `onProgress` and for the
 *  "book just reached zero" branch, which clears the clock either way. Re-arming the
 *  clock while still settling is narrower than this union: see `hasMoved` and
 *  `useBookingStatus`'s effect, which re-arms only there. */
function hasProgressed(previous: BookingCounts, current: BookingCounts): boolean {
  return hasMoved(previous, current) || uncertaintyResolved(previous, current);
}

/** `counts.oldestCreated` as epoch milliseconds, or `null` when the tick fetched no
 *  rows or carried nothing parseable — the discipline `use-parse-import-queries.ts`'s
 *  `useElapsedClock` uses for a resource's own `created`, applied to the oldest of
 *  several rows instead of one. */
function oldestPendingStartMs(counts: BookingCounts): number | null {
  if (counts.oldestCreated == null) return null;
  const ms = new Date(counts.oldestCreated).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Whether this portfolio's positions and NAV are final, polled while they are not.
 *
 * One query, keyed by `portfolioDetailQueryKeys.booking`. It runs on mount — a portfolio
 * opened while someone else's import is booking shows the marker too — and then on its
 * own clock: `FAST_POLL_MS` for the first `FAST_POLL_WINDOW_MS` of settling, `SLOW_POLL_MS`
 * after, stopping at `POLL_TIMEOUT_MS`. Together with the import poll this is the page's
 * second sanctioned exception to its no-background-refetch rule
 * (`PORTFOLIO_DETAIL_REFETCH_OPTIONS`): both are bounded and both exist to watch a
 * pipeline the reader started. Nothing invalidates this query's key to re-check it — the
 * reader's own actions (`check`) and its own clock are the only two triggers; see `check`.
 *
 * The clock measures time since the book last moved or the reader last acted, held in a
 * ref, not in the query's data: the counts a tick returns say nothing about how long they
 * have been non-zero. `refetchInterval` only READS the ref — every write happens in the
 * counts effect below or in `check`, the same as `use-parse-import-queries.ts` computes
 * its own clock before calling `useQuery` rather than writing back into
 * `refetchInterval`'s own evaluation. A tick that starts settling before the effect has
 * run yet reads a null anchor here, which resolves to zero elapsed — exactly the cadence
 * a fresh settle should get, so the one-render gap costs nothing.
 *
 * The ref resets to null when the counts stop settling, re-arms on any tick that shows
 * real movement (`hasMoved`: a count fell or an id left a page), and re-arms on `check`
 * — so a large import booking steadily for minutes never trips the slow or timed-out
 * copy, and a trade recorded, an import settled, or a declaration made after the poll
 * stopped restarts the wait instead of leaving it stuck past the ceiling. A tick that
 * only resolves an uncertainty (`uncertaintyResolved`: a cursor clearing, a failed
 * import-read leg recovering) still refreshes the figures once through `onProgress`, but
 * does NOT re-arm this ref: a leg that flaps — fails, recovers, fails, recovers — must
 * not keep resetting the clock on a book that, from the reader's perspective, is not
 * moving at all. See `hasMoved` and `uncertaintyResolved`'s own doc comments.
 *
 * The one case none of those three re-arm points cover is the FIRST tick of a fresh
 * settle — including the first tick after a remount, since a route change and back nulls
 * this ref the same way a genuinely new settle does (see the `[portfolioId]` effect
 * below, and unmounting the marker's own screen has the same effect via component
 * teardown). Seeding that tick from `Date.now()` measures time since THIS COMPONENT
 * INSTANCE first observed the book settling, not since the book actually started
 * settling — so a book stuck at an unchanging count reads as freshly-settling again on
 * every remount, exactly hiding the staleness this feature exists to surface. The seed
 * instead anchors to `oldestPendingStartMs`, the server's own `created` on the oldest
 * still-pending row (`BookingCounts.oldestCreated`, oldest-first per `readBookingCounts`)
 * — a fact about the book, not about this component instance, so it reads the same
 * whether this is the first mount ever or the fifth remount of an already-stuck one.
 * Falling back to `Date.now()` only when no server timestamp exists yet (the
 * uncounted-partial case, or before the first answer lands) matches
 * `useElapsedClock`'s own fallback. Every later re-arm point (progress, `check`) still
 * writes local watch time on top of that seed — once the book is known to be moving or
 * the reader has acted, local time is exactly what should be measured next.
 *
 * Unknown is not none: a failed check reports `isUnknown` and `counts: null`, even when
 * an earlier tick's counts are still cached, so nothing on the page can read a failure
 * as "settled". The poll stops on an error the same way the import poll does, and the
 * marker's Check again is the way out.
 *
 * Incomplete is neither: the tick answered, the trade and snapshot reads are proven
 * empty and unpaginated, and only a running-import read failed (`isIncomplete`). The
 * figures on the page are shown as they are — an empty table is genuinely empty — and
 * no poll runs, because nothing is known to be settling; the marker says the running
 * imports could not be checked and offers Check again. A failed import read on a tick
 * that IS settling is not a state of its own: the marker stays settling, keeps polling,
 * and names the unchecked imports beside the counts it does have.
 */
export function useBookingStatus({
  sessionId,
  accountScope,
  portfolioId,
  onProgress,
}: UseBookingStatusOptions): BookingStatus {
  const settlingSinceRef = useRef<number | null>(null);
  const previousCountsRef = useRef<BookingCounts | null>(null);
  // The previous portfolio's clock and counts must not carry into this one: a route
  // change to another portfolio swaps the query key, and its first answer would
  // otherwise be compared against counts that belong to a different book.
  useEffect(() => {
    settlingSinceRef.current = null;
    previousCountsRef.current = null;
  }, [portfolioId]);

  const settlingElapsedMs = (): number | null =>
    settlingSinceRef.current == null ? null : Date.now() - settlingSinceRef.current;

  const query = useQuery({
    queryKey: portfolioDetailQueryKeys.booking(portfolioId ?? ''),
    queryFn: () => readBookingCounts(sessionId!, accountScope!, portfolioId!),
    enabled: !!sessionId && !!accountScope && !!portfolioId,
    retry: PORTFOLIO_DETAIL_RETRY,
    // Read-only: see the hook's own doc comment above for why this callback never
    // writes `settlingSinceRef` itself.
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      const counts = query.state.data as BookingCounts | undefined;
      if (counts == null) return false;
      if (!isSettlingCounts(counts)) return false;
      const elapsed = settlingElapsedMs() ?? 0;
      if (elapsed > POLL_TIMEOUT_MS) return false;
      return elapsed > FAST_POLL_WINDOW_MS ? SLOW_POLL_MS : FAST_POLL_MS;
    },
    ...PORTFOLIO_DETAIL_REFETCH_OPTIONS,
  });

  // Read through a ref so a parent re-render handing in a fresh callback cannot itself
  // count as progress — the same shape `useCsvImport` gives `onImported`.
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  const counts = query.isError ? null : (query.data ?? null);
  useEffect(() => {
    if (counts == null) return;
    const previous = previousCountsRef.current;
    previousCountsRef.current = counts;
    // Progress is a property of the tick, not of where it lands: a count that fell is
    // progress whether it fell to one or to zero, and reaching zero also clears the
    // clock, since there is nothing left to settle. A tick whose previous answer was
    // not itself settling never counts as progress — that covers both "first tick of a
    // fresh settle" (previous null or already settled) and "settling to unknown", which
    // never reaches this effect at all: an error answer resolves `counts` to `null`
    // above, so the effect returns before either branch runs.
    const wasSettling = previous != null && isSettlingCounts(previous);
    // Movement re-arms the clock; a cleared uncertainty alone never does (see the
    // hook's own doc comment above and `uncertaintyResolved`'s) — both still count for
    // `onProgress`, which only decides whether to refresh the figures.
    const moved = wasSettling && hasMoved(previous, counts);
    const progressed = wasSettling && hasProgressed(previous, counts);
    if (!isSettlingCounts(counts)) {
      settlingSinceRef.current = null;
      if (progressed) onProgressRef.current?.();
      return;
    }
    if (!wasSettling) {
      // The first tick ever, the first tick of a fresh settle after a settled or
      // never-checked book, or the first tick after a remount: seed the clock from the
      // server's own timestamp on the oldest still-pending row, so a remount of an
      // already-stuck book does not read as freshly settling — see the hook's own doc
      // comment above. A book only just starting to settle is not "progress" over what
      // came before, so this never fires `onProgress`.
      settlingSinceRef.current ??= oldestPendingStartMs(counts) ?? Date.now();
      return;
    }
    if (moved) {
      settlingSinceRef.current = Date.now();
    }
    if (progressed) {
      onProgressRef.current?.();
    }
  }, [counts]);

  const isSettling = counts != null && isSettlingCounts(counts);
  const isIncomplete = counts != null && !isSettling && counts.failedImportReadCount > 0;
  const elapsed = isSettling ? settlingElapsedMs() : null;
  const { stalled, timedOut } = deriveTimingFlags(
    elapsed, isSettling, query.isFetching, STALL_THRESHOLD_MS, POLL_TIMEOUT_MS,
  );

  const check = () => {
    settlingSinceRef.current = Date.now();
    void query.refetch();
  };

  return {
    counts,
    isSettling,
    isSlow: stalled,
    isTimedOut: timedOut,
    isUnknown: query.isError,
    isIncomplete,
    hasAnswered: counts != null || query.isError,
    isChecking: query.isFetching,
    check,
  };
}
